import { Container, Graphics, Sprite, Text } from "pixi.js";
import type { Texture } from "pixi.js";
import type { ObstacleSnapshot } from "@frontier/protocol/contracts";

interface ResourceView {
  container: Container;
  status: Graphics;
  label: Text;
  rock: ObstacleSnapshot;
}

/** Dynamic ore is separate from the static terrain: damage never rebuilds the map. */
export class ResourceViews {
  readonly container = new Container();
  private readonly views = new Map<string, ResourceView>();
  private selected = "";

  constructor(private readonly textures: readonly [Texture, Texture, Texture]) {}

  update(obstacles: readonly ObstacleSnapshot[], selected: string): void {
    this.selected = selected;
    const ids = new Set<string>();
    for (const rock of obstacles) {
      if (!rock.resourceMaxHp || !rock.resourceHp) continue;
      ids.add(rock.id);
      let view = this.views.get(rock.id);
      if (!view) {
        const container = new Container({ x: rock.x, y: rock.y });
        const sprite = new Sprite({ texture: this.textures[2], anchor: 0.5 });
        sprite.width = rock.radius * 2.2;
        sprite.scale.y = sprite.scale.x;
        sprite.tint = 0x80c6c6;
        sprite.rotation = rock.x * 0.07 + rock.y * 0.11;
        const status = new Graphics();
        const label = new Text({ text: "РУДА", style: {
          fontFamily: "system-ui, sans-serif", fontSize: 11, fill: 0xa1e0db, letterSpacing: 2,
        } });
        label.anchor.set(0.5);
        label.y = rock.radius + 22;
        container.addChild(sprite, status, label);
        view = { container, status, label, rock };
        this.views.set(rock.id, view);
        this.container.addChild(container);
      }
      view.rock = rock;
      this.draw(view);
    }
    for (const [id, view] of this.views) {
      if (ids.has(id)) continue;
      view.container.destroy({ children: true });
      this.views.delete(id);
    }
  }

  select(id: string): void {
    this.selected = id;
    for (const view of this.views.values()) this.draw(view);
  }

  private draw(view: ResourceView): void {
    const rock = view.rock;
    const selected = rock.id === this.selected;
    const fraction = (rock.resourceHp ?? 0) / (rock.resourceMaxHp ?? 1);
    view.status.clear().circle(0, 0, rock.radius + 4)
      .stroke({ color: 0x80e8dd, width: selected ? 2 : 1, alpha: selected ? 0.9 : 0.3 });
    if (selected || fraction < 1) {
      view.status.rect(-25, -rock.radius - 14, 50, 4).fill({ color: 0x193535 });
      view.status.rect(-25, -rock.radius - 14, 50 * fraction, 4).fill({ color: 0x80e8dd });
    }
    view.label.text = selected ? `РУДА · ${Math.ceil(rock.resourceHp ?? 0)}` : "РУДА";
  }
}
